import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { getTeacherWorkItems, type TeacherWorkItem } from "../../../service/teacher-portal"
import {
  getFinalTermPrep,
  saveMarksGrid,
  getMarksGrid,
  type FinalTermPrep,
  type FinalTermRow,
  type FinalTermPct,
} from "../../../service/marks-entry"

const computeCombined = (row: FinalTermRow, termPcts: FinalTermPct[], finalEntry: string): number => {
  let total = 0
  for (const tp of termPcts) {
    let val: number
    if (tp.isFinal) {
      const n = parseFloat(finalEntry)
      val = Number.isFinite(n) ? n : 0
    } else {
      const prior = row.prior[tp.termId]
      val = typeof prior === "number" ? prior : 0
    }
    total += (val * tp.percentage) / 100
  }
  return total
}

const TeacherFinalTerm = () => {
  const [items, setItems] = useState<TeacherWorkItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedKey, setSelectedKey] = useState("")
  const [prep, setPrep] = useState<FinalTermPrep | null>(null)
  const [loadingPrep, setLoadingPrep] = useState(false)

  useEffect(() => {
    getTeacherWorkItems()
      .then((r) => setItems(r.data.items.filter((i) => i.is_final)))
      .catch(() => toast.error("Failed to load assignments"))
      .finally(() => setLoading(false))
  }, [])

  const loadPrep = async (item: TeacherWorkItem) => {
    const key = `${item.course_structure_id}|${item.exam_term_id}`
    setSelectedKey(key)
    setPrep(null)
    if (!item.breakdown_id) {
      toast.error("No result breakdown linked to this assignment. Contact admin.")
      return
    }
    setLoadingPrep(true)
    try {
      const r = await getFinalTermPrep({
        courseStructureId: item.course_structure_id,
        breakdownId: item.breakdown_id,
      })
      setPrep(r.data)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load final term data")
    } finally {
      setLoadingPrep(false)
    }
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Final Term Carryover</h3>
          <p className="text-muted mb-0">
            Final Term grade = weighted blend of all term marks. Prior terms are read-only.
          </p>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : items.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-5 text-muted">
              No Final Term assignments right now.
            </div>
          </div>
        ) : (
          <>
            <div className="card mb-3">
              <div className="card-body">
                <label className="form-label fw-semibold">Choose a Final Term assignment</label>
                <select
                  className="form-select"
                  value={selectedKey}
                  onChange={(e) => {
                    const it = items.find(
                      (i) => `${i.course_structure_id}|${i.exam_term_id}` === e.target.value
                    )
                    if (it) loadPrep(it)
                  }}
                >
                  <option value="">— Pick one —</option>
                  {items.map((it) => (
                    <option
                      key={`${it.course_structure_id}|${it.exam_term_id}`}
                      value={`${it.course_structure_id}|${it.exam_term_id}`}
                    >
                      {it.class_name}
                      {it.section_name ? ` · ${it.section_name}` : ""} — {it.subject_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loadingPrep && <p className="text-muted">Loading…</p>}
            {prep && !loadingPrep && (
              <FinalTermGrid
                prep={prep}
                item={items.find((i) => `${i.course_structure_id}|${i.exam_term_id}` === selectedKey)!}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

const FinalTermGrid = ({
  prep,
  item,
}: {
  prep: FinalTermPrep
  item: TeacherWorkItem
}) => {
  const [entries, setEntries] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const isLocked = prep.breakdown.status === "Published" || prep.breakdown.status === "Locked"
  const priorPcts = prep.term_percentages.filter((t) => !t.isFinal)
  const finalPct = prep.term_percentages.find((t) => t.isFinal)

  const save = async () => {
    setSaving(true)
    try {
      const gridRes = await getMarksGrid({
        session: "",
        class: "",
        section: "",
        breakdown: item.breakdown_id || item.exam_term_id,
        courseStructureId: item.course_structure_id,
      })
      const awardSlipId = gridRes?.data?.awardSlipId
      if (!awardSlipId) throw new Error("Could not find award slip")

      const entriesToSave = Object.entries(entries)
        .filter(([, v]) => v !== "")
        .map(([enrollmentId, v]) => ({
          enrollmentId,
          subCategory: null,
          obtainedMarks: parseFloat(v),
          isAbsent: false,
          isNa: false,
        }))

      if (!entriesToSave.length) { toast.info("No changes"); return }
      await saveMarksGrid({ awardSlipId, entries: entriesToSave })
      toast.success("Saved & recomputed")
      setEntries({})
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="card-title mb-0">{prep.course_structure.subject_name}</h6>
          <small className="text-muted">{prep.rows.length} students</small>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={saving || isLocked || Object.keys(entries).length === 0}
        >
          {saving ? "Saving…" : "Save & Recompute"}
        </button>
      </div>

      {prep.term_percentages.length > 0 && (
        <div className="alert alert-info m-3 py-2 mb-0">
          <strong>Term weights: </strong>
          {prep.term_percentages.map((t) => (
            <span key={t.termId} className="me-3">
              {t.termName}: <strong>{t.percentage}%</strong>
              {t.isFinal && <span className="badge bg-primary ms-1">Final</span>}
            </span>
          ))}
        </div>
      )}

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                {priorPcts.map((t) => (
                  <th key={t.termId}>
                    {t.termName}
                    <small className="d-block text-muted">({t.percentage}%)</small>
                  </th>
                ))}
                <th>
                  Final Exam
                  {finalPct && <small className="d-block text-muted">({finalPct.percentage}%)</small>}
                </th>
                <th>Combined</th>
              </tr>
            </thead>
            <tbody>
              {prep.rows.map((row) => {
                const finalEntered =
                  entries[row.student.id] ??
                  (row.final_exam_total != null ? String(row.final_exam_total) : "")
                const combined = computeCombined(row, prep.term_percentages, finalEntered)
                return (
                  <tr key={row.student.id}>
                    <td>
                      <div className="small fw-semibold">{row.student.full_name}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Roll: {row.student.roll_number}
                      </div>
                    </td>
                    {priorPcts.map((t) => {
                      const val = row.prior[t.termId]
                      return (
                        <td key={t.termId} className="bg-light text-center small">
                          {val == null ? (
                            <span className="text-warning">missing</span>
                          ) : val === "AB" ? (
                            <span className="badge bg-warning text-dark">AB</span>
                          ) : val === "NA" ? (
                            <span className="badge bg-secondary">NA</span>
                          ) : typeof val === "number" ? (
                            val.toFixed(2)
                          ) : (
                            val
                          )}
                        </td>
                      )
                    })}
                    <td className="p-1">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={prep.course_structure.total_marks}
                        disabled={isLocked}
                        value={finalEntered}
                        onChange={(e) =>
                          setEntries((s) => ({ ...s, [row.student.id]: e.target.value }))
                        }
                        className="form-control form-control-sm"
                        style={{ width: 90 }}
                      />
                    </td>
                    <td className="fw-semibold text-center small">{combined.toFixed(2)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default TeacherFinalTerm
